import AbstractSubject from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import type { ReviewCollectionResponse, ReviewItem } from './ReviewCollection';
import ReviewCollection from './ReviewCollection';

export type InitialState = {
    reviews: ReviewItem[];
    total: number;
};

export default class ReviewListItems extends AbstractSubject {
    static #instance: ReviewListItems | null = null;

    #reviews: ReviewItem[] = [];

    public static getInstance(
        initialState: InitialState = {
            reviews: [],
            total: 0
        }
    ): ReviewListItems {
        if (ReviewListItems.#instance === null) {
            ReviewListItems.#instance = new ReviewListItems();

            ReviewListItems.#instance.#reviews = initialState.reviews;
            ReviewListItems.#instance.#total = initialState.total;
        }

        return ReviewListItems.#instance;
    }

    public static destroyInstance(): void {
        ReviewListItems.#instance = null;
    }

    readonly #collection = ReviewCollection.getInstance();

    #houseId: string | null = null;

    set houseId(value: string | null) {
        this.#houseId = value;
    }

    get reviews(): ReviewItem[] {
        return this.#reviews;
    }

    #total = 0;

    get total(): number {
        return this.#total;
    }

    #limit = 5;

    get limit(): number {
        return this.#limit;
    }

    #skip = 0;

    get skip(): number {
        return this.#skip;
    }

    readonly #pageSize = 5;

    get pageSize(): number {
        return this.#pageSize;
    }

    #currentPage = 1;

    get currentPage(): number {
        return this.#currentPage;
    }

    get reviewsForCurrentPage(): ReviewItem[] {
        const start = (this.#currentPage - 1) * this.#pageSize;
        return this.#reviews.slice(start, start + this.#pageSize);
    }

    public goToPage(page: number): void {
        const totalPages = Math.ceil(this.#total / this.#pageSize) || 1;
        const clamped = Math.max(1, Math.min(page, totalPages));
        if (this.#currentPage !== clamped) {
            this.#currentPage = clamped;
            this.notify();
        }
    }

    public readonly fetchReviews = async (skip?: number, limit?: number): Promise<ReviewItem[]> => {
        if (!this.#houseId) {
            throw new Error('houseId is not set');
        }

        if (this.#reviews.length > 0) {
            this.#currentPage = 1;
            this.notify();
            return this.#reviews;
        }

        if (skip !== undefined) {
            this.#skip = skip;
        }

        if (limit !== undefined) {
            this.#limit = limit;
        }

        const reviewResponse: ReviewCollectionResponse = await this.#collection.retrieve({
            houseId: this.#houseId,
            skip: this.#skip,
            limit: this.#limit
        });

        this.setResults(
            reviewResponse.reviews,
            reviewResponse.total,
            reviewResponse.limit,
            reviewResponse.skip
        );

        return reviewResponse.reviews;
    };

    public setResults(reviews: ReviewItem[], total: number, limit: number, skip: number): void {
        this.#reviews = reviews;
        this.#total = total;
        this.#limit = limit;
        this.#skip = skip;
        this.#currentPage = 1;
        this.notify();
    }
}
